import { UserGearIconRaw } from '@tableau/icons/bundle';
import { Colors } from '@tableau/icons/util/Colors';
import { SiteUrls } from '@tableau/shared-urls';
import Fuse from 'fuse.js';

import { escapeRegexp } from '~src/commonInfra/components/FormattedMatch';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { Tableau } from '~src/commonInfra/utils/TableauLog';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';

export type BeaconComponentConfig = {
  command: () => string;
  deeplinkId: string;
  url: (props?: any) => string;
  tags?: () => Array<string>;
  isAdmin: boolean;
  isVisible?: () => boolean; // optional, if no requirements are specified will default to true
};

export type ResolvedBeaconComponentConfig = {
  command: string;
  deeplinkId: string;
  url: string;
  icon: string;
  tags?: string[];
};

// If the BEACON_ICON_SIZE has to be modified here, make sure to update the size in
// 1. the CommandSuggestionViewModel class located in the CommandSuggestionViewModel.ts file within the omnibox-microview repository:
//    https://gitlab.tableausoftware.com/semantic-search/omnibox-microview
// 2. the typescript type for the icon size in the BeaconComponentConfig type
export const BEACON_ICON_SIZE = 24;

const MAX_COMPONENT_ID_SIZE = 64;

/**
 * A helper function, used as a workaround to get stronger types when passing specs to createBeaconComponentRegistry
 * @param specs
 */
export function beaconComponentRegistryEntry(specs: BeaconComponentConfig) {
  return specs;
}

// Ensure that the provided deep link id is alphanumeric characters separated by dashes
export function isDeepLinkIdValid(deepLinkId: string) {
  return deepLinkId && deepLinkId.length <= MAX_COMPONENT_ID_SIZE &&
    new RegExp('^[A-Za-z1-9]+(?:[-][A-Za-z1-9]+)*$').test(deepLinkId);
}

function getSiteURL(sessionInfo: SessionManagementService) {
  return new SiteUrls({
    siteUrlName: sessionInfo.sessionSiteUrlName(),
    queryString: window.location.search
  });
}

export function getSiteSettingsURL(sessionInfo: SessionManagementService) {
  return getSiteURL(sessionInfo).settingsUrl();
}

export function getUserAccountSettingsURL(sessionInfo: SessionManagementService) {
  const sessionUserInfo = sessionInfo.sessionUserInfo();
  return getSiteURL(sessionInfo).userSettingsUrl(sessionUserInfo.username, sessionUserInfo.domainName);
}

export function processRegistry(componentRegistry: BeaconComponentConfig[]) {
  const sessionManagementService = getSessionManagementServiceInstance();
  const isAdmin = sessionManagementService.isOnlineEnvironment()
    ? sessionManagementService.isSessionSiteOrServerAdmin()
    : sessionManagementService.isSessionServerAdmin();

  const componentsWithValidId = componentRegistry.filter(registryEntry => {
    const isIdValid = isDeepLinkIdValid(registryEntry.deeplinkId);
    if (!isIdValid) {
      Tableau.Logger().error(`Dropping component configuration from the registry. ` +
        `Provided deep link id ${registryEntry.deeplinkId} does not match the expected pattern.`);
    }
    return isIdValid;
  });

  const componentsWhichAreVisible = componentsWithValidId.filter(component => isVisible(component));

  const componentsWithUserPermissions = isAdmin ? componentsWhichAreVisible :
    componentsWhichAreVisible.filter(component => component.isAdmin === isAdmin);

  const updatedRegistry: ResolvedBeaconComponentConfig[] = [];
  componentsWithUserPermissions.forEach(item => {
    const baseUrl = WindowLocationService.getOrigin();
    const updatedItem: ResolvedBeaconComponentConfig = {
      command: item.command(),
      deeplinkId: item.deeplinkId,
      url: baseUrl + item.url({ sessionInfo: sessionManagementService }) + '?deeplink=' + item.deeplinkId,
      icon: UserGearIconRaw(BEACON_ICON_SIZE, Colors.Default),
      tags: item.tags?.()
    };
    updatedRegistry.push(updatedItem);
  });

  return updatedRegistry;
}

export function highlightSearchResults(searchResults: ResolvedBeaconComponentConfig[], highlightTerm: string) {
  // the user's query might contain special characters which should be processed
  // literally in the regex. Escape all special characters
  const escapedHighlightTerm = escapeRegexp(highlightTerm);

  // the fuzzy matching algorithm results in very inaccurate highlighting
  // use a regular expression to highlight the words that are in the user's search term
  // example of how the regular expression is formatted:
  // for the search term "personal access token", the regex is (personal)|(access)|(token)
  const regexGroupsForEachWord = escapedHighlightTerm.trim().split(' ').map(word => `(${word})`);
  const highlightRegExp = new RegExp(regexGroupsForEachWord.join('|'), 'gi');

  // run a global regex match for each beacon component and replace all of the matching words with bolded html
  searchResults.forEach(searchResult => {
    searchResult.command = searchResult.command.replace(
      highlightRegExp,
      matchedWord => `<em>${matchedWord}</em>`
    );
  });

  return searchResults;
}

export function searchBeaconComponents(processedRegistry: ResolvedBeaconComponentConfig[], searchTerm: string) {
  const fuseOptions: Fuse.IFuseOptions<ResolvedBeaconComponentConfig> = {
    keys: [{ name: 'command', weight: 0.6 }, { name: 'tags', weight: 0.4 }],
    threshold: 0.2,
    shouldSort: true,
    isCaseSensitive: false,
    ignoreLocation: true
  };

  // filter the beacon components with fuzzy search
  const beaconComponentSearchResults = new Fuse(processedRegistry, fuseOptions)
    .search(searchTerm)
    .map(fuseSearchResult => fuseSearchResult.item);

  return highlightSearchResults(beaconComponentSearchResults, searchTerm);
}

export function getBeaconComponents(componentRegistry: BeaconComponentConfig[], searchTerm: string): any {
  const processedRegistry = processRegistry(componentRegistry);
  return searchBeaconComponents(processedRegistry, searchTerm);
}

/**
 * function to check if a particular component should be searchable.
 * if no requirements are specified, will default to true
 */
function isVisible(element: BeaconComponentConfig) {
  // if no function defined for visibility or defined function evaluates to true
  return !element.isVisible || element.isVisible();
}